import { useState, type FormEvent } from "react";
import { actions, listRequisitions, requestRequisitionClarification } from "@/lib/api";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RequisitionIntelligence } from "@/requisition-intelligence";
import { useData, matches, type ViewProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { AppLink } from "@/components/app-link";

export function Approvals({
  organisation,
  refresh,
  query,
  can,
  currentUserId,
  changed,
  navigate,
}: ViewProps & {
  navigate: (view: string, recordId?: string) => void;
  currentUserId: string;
}) {
  const { data, error, loading } = useData(
    () => listRequisitions(organisation.id, "submitted"),
    [organisation.id, refresh],
  );
  const [decision, setDecision] = useState<{
    id: string;
    value: "approved" | "rejected";
  }>();
  const [briefId, setBriefId] = useState<string>();
  const [clarificationId, setClarificationId] = useState<string>();
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data
    .filter((row) =>
      matches(query, row.id, row.request_no, row.title, row.profiles?.full_name),
    )
    .filter(
      (row) =>
        can("manage") ||
        !can("approve") ||
        row.requisition_approval_assignments?.some(
          (assignment) =>
            assignment.approver_id === currentUserId &&
            !assignment.reassignment_required_at,
        ),
    );
  const isAssignedApprover = (row: (typeof rows)[number]) =>
    can("approve") &&
    Boolean(
      row.requisition_approval_assignments?.some(
        (assignment) =>
          assignment.approver_id === currentUserId &&
          !assignment.reassignment_required_at,
      ),
    );
  const assignedApprovers = (row: (typeof rows)[number]) =>
    row.requisition_approval_assignments
      ?.map((assignment) =>
        assignment.reassignment_required_at
          ? `${assignment.approver?.full_name || "Former approver"} · reassignment required`
          : assignment.approver?.full_name || "Assigned member",
      )
      .join(", ") || "Not assigned";
  const lineSummary = (row: (typeof rows)[number]) =>
    row.requisition_lines
      ?.map((line) => `${line.name}: ${line.specification}`)
      .join(" · ");
  return (
    <>
      <PageHeader
        eyebrow="GOVERN"
        title="Approval queue"
        description="Value-based decisions with an attributable record."
      />
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "Request",
                "Need & specification",
                "Requester",
                "Assigned approvers",
                "Value",
                "Decision",
              ]}
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <AppLink
                      view="requisitions"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open source request ${row.request_no}`}
                      className="inline-block"
                    >
                      <strong>{row.request_no}</strong>
                      <small className="block text-[var(--text-secondary)]">
                        Needed {displayDate(row.needed_by)}
                      </small>
                    </AppLink>
                  </td>
                  <td>
                    <strong>{row.title}</strong>
                    <small className="block max-w-lg text-[var(--text-secondary)]">
                      {lineSummary(row)}
                    </small>
                  </td>
                  <td>{row.profiles?.full_name}</td>
                  <td>{assignedApprovers(row)}</td>
                  <td className="numeric">{money(row.estimated_value)}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        className="min-h-11"
                        variant="outline"
                        onClick={() => setBriefId(row.id)}
                      >
                        Review brief
                      </Button>
                      {isAssignedApprover(row) ? (
                        <>
                          <Button
                            size="sm"
                            className="min-h-11"
                            onClick={() =>
                              setDecision({ id: row.id, value: "approved" })
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            className="min-h-11"
                            variant="outline"
                            onClick={() => setClarificationId(row.id)}
                          >
                            Request clarification
                          </Button>
                          <Button
                            size="sm"
                            className="min-h-11"
                            variant="destructive"
                            onClick={() =>
                              setDecision({ id: row.id, value: "rejected" })
                            }
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <span className="self-center text-xs text-[var(--text-muted)]">
                          Read only
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Approval requests">
            {rows.map((row) => (
              <article className="surface grid gap-4 p-4" key={row.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <AppLink
                      view="requisitions"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open source request ${row.request_no}: ${row.title}`}
                      className="block"
                    >
                      <span className="eyebrow block">{row.request_no}</span>
                      <span className="mt-1 block text-base font-semibold">{row.title}</span>
                    </AppLink>
                  </div>
                  <strong className="shrink-0 text-right text-sm tabular-nums">
                    {money(row.estimated_value)}
                  </strong>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                  {lineSummary(row)}
                </p>
                <dl className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="text-[var(--text-muted)]">Needed</dt>
                    <dd className="mt-1 font-medium tabular-nums">
                      {displayDate(row.needed_by)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[var(--text-muted)]">Requester</dt>
                    <dd className="mt-1 font-medium">
                      {row.profiles?.full_name || "Not recorded"}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-[var(--text-muted)]">Assigned approvers</dt>
                    <dd className="mt-1 font-medium">{assignedApprovers(row)}</dd>
                  </div>
                </dl>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    className="col-span-2 min-h-11"
                    variant="outline"
                    onClick={() => setBriefId(row.id)}
                  >
                    Review approval brief
                  </Button>
                  {isAssignedApprover(row) ? (
                    <>
                      <Button
                        className="min-h-11"
                        onClick={() =>
                          setDecision({ id: row.id, value: "approved" })
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        className="min-h-11"
                        variant="destructive"
                        onClick={() =>
                          setDecision({ id: row.id, value: "rejected" })
                        }
                      >
                        Reject
                      </Button>
                      <Button
                        className="col-span-2 min-h-11"
                        variant="outline"
                        onClick={() => setClarificationId(row.id)}
                      >
                        Request clarification
                      </Button>
                    </>
                  ) : (
                    <p className="col-span-2 text-xs text-[var(--text-muted)]">
                      Read-only view — this request is not assigned to you.
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <Empty
          title="Approval queue is clear"
          description="Submitted requisitions appear here automatically."
        />
      )}
      <DecisionDialog
        value={decision}
        onOpenChange={(open) => !open && setDecision(undefined)}
        organisationId={organisation.id}
        onDone={changed}
      />
      <ClarificationRequestDialog
        requisitionId={clarificationId}
        organisationId={organisation.id}
        onOpenChange={(open) => !open && setClarificationId(undefined)}
        onDone={changed}
      />
      <RequisitionIntelligence
        organisationId={organisation.id}
        requisitionId={briefId}
        open={Boolean(briefId)}
        onOpenChange={(open) => !open && setBriefId(undefined)}
        onNavigate={navigate}
        canDecide={Boolean(briefId && rows.find((row) => row.id === briefId && isAssignedApprover(row)))}
        onDecision={(value) => {
          if (!briefId) return;
          setDecision({ id: briefId, value });
          setBriefId(undefined);
        }}
        onRequestClarification={() => {
          if (!briefId) return;
          setClarificationId(briefId);
          setBriefId(undefined);
        }}
      />
    </>
  );
}
function ClarificationRequestDialog({
  requisitionId,
  organisationId,
  onOpenChange,
  onDone,
}: {
  requisitionId?: string;
  organisationId: string;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!requisitionId) return;
    const message = String(new FormData(event.currentTarget).get("message") || "").trim();
    setBusy(true);
    setError("");
    try {
      await requestRequisitionClarification(organisationId, requisitionId, message);
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={Boolean(requisitionId)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="DECISION CLARIFICATION"
          title="Request clarification"
          description="Ask one specific evidence-based question. The requisition remains submitted and no approval decision is inferred."
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Clarification required
            <Textarea name="message" required minLength={10} maxLength={4000} />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Recording…" : "Request clarification"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({
  value,
  onOpenChange,
  organisationId,
  onDone,
}: {
  value?: { id: string; value: "approved" | "rejected" };
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!value) return;
    const comments = String(
      new FormData(event.currentTarget).get("comments") || "",
    );
    setBusy(true);
    setError("");
    try {
      await actions.decideRequisition(
        organisationId,
        value.id,
        value.value,
        comments,
      );
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(value)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="APPROVAL DECISION"
          title={`${titleCase(value?.value)} requisition`}
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Decision note
            <Textarea name="comments" required={value?.value === "rejected"} />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Button
              type="button"
              className="min-h-11"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="min-h-11"
              variant={value?.value === "rejected" ? "destructive" : "default"}
              disabled={busy}
            >
              Confirm
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
